import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import {
  Tab,
  Tabs,
  TabList,
  TabPanel,
} from '@lifeomic/chroma-react/components/Tabs';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Checkbox } from '@lifeomic/chroma-react/components/Checkbox';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { SparkleOutput, Trash } from '@lifeomic/chromicons';
import { useHistory, useParams } from 'react-router-dom';
import SubjectViewerTableModule from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/Table';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import useServiceAuth from '@lifeomic/phc-web-toolkit/src/hooks/useServiceAuth';
import {
  useActiveAccountId,
  useActiveProjectId,
  useNotify,
  useFeatures,
  useMixpanel,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { useApiClient } from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';
import type { DocumentExtractionV2Invocation } from '@lifeomic/react-client';
import type { TableConfiguration } from '@lifeomic/chroma-react/components/TableModule';
import { ConfiguredMomentFormat } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/util/momentFormats';
import {
  Category,
  DataSourceType,
  TableModuleColumnConfig,
  Tag,
} from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/util/types';
import { TimeFilterConfig } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/hooks/usePatientViewerFhirSearch';
import {
  DashboardConfig,
  ModuleConfig,
} from '@lifeomic/phc-web-toolkit/src/components/LayoutConfiguration/types';
import urls from '../../../../util/urls';
import MemberDetail from './index';
import { memberDetailMessages } from './messages';
import { NotesContent } from './Notes';
import { MedicationsContent } from './Medications';
import { ProceduresContent } from './Procedures';
import { ConditionsContent } from './Conditions';
import { AllergiesContent } from './Allergies';
import { ImmunizationsContent } from './Immunizations';
import {
  DOCUMENT_EXTRACTION_V2_SUMMARY_DOCUMENT_TYPE,
  DOCUMENT_EXTRACTION_V2_SUMMARY_SOURCE_TYPE,
  FHIR_DOCUMENT_TYPE_TAG_SYSTEM,
  FHIR_SOURCE_TYPE_TAG_SYSTEM,
  canDeleteMemberDocument,
  shouldRenderInMemberDocumentsTable,
} from './memberDocumentSource';
import { DocumentExtractionV2Modal } from './DocumentExtractionV2Modal';
import { useMemberFileValidationResolution } from './MemberFileValidationResolution';
import { runBulkDocumentExtractionV2Invoke } from './documentExtractionV2BulkInvoke';
import {
  bucketDocumentExtractionSelectionCount,
  classifyBulkDocumentExtractionOutcome,
} from './documentExtractionV2Analytics';
import { isDocumentExtractionV2InFlight } from './DocumentExtractionV2Tasks';
import {
  getDocumentExtractionTask,
  loadDurableExtractedDocumentIds,
} from './documentExtractionV2StatusLoading';

const documentReferenceFhirService = new FhirService('DocumentReference');

export const HIDDEN_BY_VALIDATION_JMES_PATH =
  "length(meta.tag[?system=='http://lifeomic.com/fhir/resource-visibility' && code=='clinic-only']) > `0` && length(meta.tag[?system=='http://lifeomic.com/fhir/file-ownership-validation-status' && code=='invalid']) > `0` && 'Yes' || 'No'";

export const AI_VALIDATION_STATUS_JMES_PATH =
  "meta.tag[?system=='http://lifeomic.com/fhir/file-ownership-validation-status'] | [0].code || 'not scanned'";

type DocumentsModuleComponent = {
  displayAsDuration: boolean;
  ocrViewerBasePath: string;
  dataSource: DataSourceType;
  columns: TableModuleColumnConfig[];
  momentFormat: ConfiguredMomentFormat;
  pageSize: number;
  useTagFilter: boolean;
  useNotTagFilter: boolean;
  notTagFilters: Tag[];
  categories: Category[];
  useCategoryFilter: boolean;
  timeFilterProps: TimeFilterConfig;
  applyDateFilter: boolean;
  filters: Tag[];
};

const documentsModuleConfig: ModuleConfig<DocumentsModuleComponent> = {
  version: 2,
  module: {
    key: 'b6bd6742-ce71-4e71-b79e-6ecb8a5bfc04',
    moduleType: '83387f5f-0e8c-456d-b528-7c1f62bcc5e4',
    title: 'Member Documents',
    layout: {
      w: 12,
      h: 20,
      x: 0,
      y: 0,
      minW: 2,
      minH: 0,
      moved: false,
      isResizable: true,
    },
    hasDynamicHeight: true,
    hasDynamicWidth: false,
    preCollapsedHeight: undefined,
    isHeaderlessModule: false,
  },
  component: {
    ocrViewerBasePath: '/project/ocr/viewer',
    dataSource: 'DOCUMENT_REFERENCE_DATA_SOURCE_TYPE',
    columns: [
      {
        label: 'Title',
        key: '88f766a2-e196-4474-98de-ed7b36c8c161',
        jmesPath: 'type.text',
      },
      {
        key: 'fde4b1e4-a6b2-47dd-b414-c11b9161e4c7',
      },
      {
        label: 'Source',
        key: '88f766a2-e196-4474-98de-ed7b36c8c161',
        jmesPath: "meta.tag[?system=='http://lifeomic.com/fhir/source-type']",
        jmesResultParser: 'coding',
      },
      {
        label: 'Type',
        key: '88f766a2-e196-4474-98de-ed7b36c8c161',
        jmesPath: "meta.tag[?system=='http://lifeomic.com/fhir/document-type']",
        jmesResultParser: 'coding',
      },
      {
        key: '6fb08933-f452-4923-83de-603b3f66478a',
        label: 'Date',
      },
      {
        key: '88f766a2-e196-4474-98de-ed7b36c8c161',
        jmesPath:
          "meta.tag[?system=='http://lifeomic.com/fhir/resource-visibility' && code=='clinic-only'] | [0].code && 'No' || 'Yes'",
        label: 'Viewable by Member?',
      },
      {
        key: '88f766a2-e196-4474-98de-ed7b36c8c161',
        jmesPath: HIDDEN_BY_VALIDATION_JMES_PATH,
        label: 'Hidden by Validation?',
      },
      {
        key: '88f766a2-e196-4474-98de-ed7b36c8c161',
        jmesPath: AI_VALIDATION_STATUS_JMES_PATH,
        label: 'AI Validation Status',
      },
    ],
    displayAsDuration: false,
    momentFormat: 'M/D/YYYY',
    pageSize: 200,
    useTagFilter: false,
    useNotTagFilter: true,
    notTagFilters: [
      {
        system: 'http://lifeomic.com/fhir/source',
        code: 'LifeOmic GA4GH Service',
      },
      {
        system: FHIR_DOCUMENT_TYPE_TAG_SYSTEM,
        code: DOCUMENT_EXTRACTION_V2_SUMMARY_DOCUMENT_TYPE,
      },
      {
        system: FHIR_SOURCE_TYPE_TAG_SYSTEM,
        code: DOCUMENT_EXTRACTION_V2_SUMMARY_SOURCE_TYPE,
      },
    ],
    categories: [],
    useCategoryFilter: false,
    timeFilterProps: {
      DocumentReference: 'created',
    },
    applyDateFilter: false,
    filters: [],
  },
};

const documentsDashboardConfig: DashboardConfig = {
  version: 1,
  modules: [documentsModuleConfig],
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  tabsContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  tabsRoot: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  tabListWrapper: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
  },
  tabList: {
    flexShrink: 0,
    alignSelf: 'flex-start',
    display: 'inline-flex',
  },
  tabPanel: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    backgroundColor: theme.palette.background.paper,
    position: 'relative',
  },
  documentsToolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  documentsTable: {
    '& thead tr th:first-child, & tbody tr td:first-child': {
      width: theme.pxToRem(40),
      minWidth: theme.pxToRem(40),
      maxWidth: theme.pxToRem(40),
      paddingLeft: theme.spacing(1),
      paddingRight: 0,
      textAlign: 'center',
    },
  },
  tableCell: {
    whiteSpace: 'unset',
  },
  selectionColumn: {
    width: theme.pxToRem(40),
    minWidth: theme.pxToRem(40),
    maxWidth: theme.pxToRem(40),
    paddingLeft: theme.spacing(1),
    paddingRight: 0,
    backgroundColor: 'inherit',
  },
  selectionCheckbox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    '& label': {
      margin: 0,
    },
  },
}));

type DocumentsContentProps = {
  member: fhir.Patient | null;
  fallback: React.ReactNode;
};

type DocumentsTab =
  | 'documents'
  | 'notes'
  | 'medications'
  | 'procedures'
  | 'conditions'
  | 'allergies'
  | 'immunizations';

const isDocumentsTab = (value: string | undefined): value is DocumentsTab =>
  value === 'documents' ||
  value === 'notes' ||
  value === 'medications' ||
  value === 'procedures' ||
  value === 'conditions' ||
  value === 'allergies' ||
  value === 'immunizations';

const getDocumentReferenceFromTableRow = (row: { rowMeta?: unknown }) => {
  const resource = (row.rowMeta as { resource?: fhir.Resource } | undefined)
    ?.resource;
  return resource?.resourceType === 'DocumentReference'
    ? (resource as fhir.DocumentReference)
    : null;
};

const areStringArraysEqual = (left: string[], right: string[]) =>
  left.length === right.length &&
  left.every((value, index) => value === right[index]);

type DocumentsExtractionStatus =
  | 'none'
  | 'pending'
  | 'processing'
  | 'failed'
  | 'done';

const getDocumentExtractionStatus = ({
  task,
  hasDurableExtraction,
}: {
  task: DocumentExtractionV2Invocation | null | undefined;
  hasDurableExtraction: boolean;
}): DocumentsExtractionStatus => {
  if (task && isDocumentExtractionV2InFlight(task)) {
    return task.status === 'processing' ? 'processing' : 'pending';
  }
  // Persisted FHIR evidence is authoritative: it outlives task TTL and a
  // failed re-run, so it must win over `failed`/`expired`/missing tasks to
  // avoid showing "Not extracted" for documents that were extracted.
  if (hasDurableExtraction) {
    return 'done';
  }
  if (task?.status === 'completed') {
    return 'done';
  }
  if (task?.status === 'failed') {
    return 'failed';
  }
  return 'none';
};

export const DocumentsContent: React.FC<DocumentsContentProps> = ({
  member,
  fallback,
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();
  const accountId = useActiveAccountId();
  const projectId = useActiveProjectId();
  const apiClient = useApiClient();
  const serviceAuth = useServiceAuth();
  const hasFeature = useFeatures();
  const [notify] = useNotify();
  const { track } = useMixpanel();
  const [documentsListVersion, setDocumentsListVersion] = useState(0);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);
  const [visibleDocumentIds, setVisibleDocumentIds] = useState<string[]>([]);
  const [documentExtractionTasks, setDocumentExtractionTasks] = useState<
    DocumentExtractionV2Invocation[]
  >([]);
  const [isExtractionStatusLoading, setIsExtractionStatusLoading] =
    useState(false);
  const [durableExtractedDocumentIds, setDurableExtractedDocumentIds] =
    useState<string[]>([]);
  const [isBulkExtracting, setIsBulkExtracting] = useState(false);
  const [deleteTarget, setDeleteTarget] =
    useState<fhir.DocumentReference | null>(null);
  const [extractionTarget, setExtractionTarget] =
    useState<fhir.DocumentReference | null>(null);
  const canExtractDocuments = true;
  const canShowExtractionStatusColumn = hasFeature(
    'documentExtractionV2StatusColumn',
  );
  const [deleteDocument, deleteState, resetDelete] = useService(
    documentReferenceFhirService.delete,
    { rethrowErrors: true },
  );
  const history = useHistory();
  const { documentsTab, memberId: memberIdFromRoute } = useParams<{
    documentsTab?: string;
    memberId?: string;
  }>();

  const selectedTab: DocumentsTab = isDocumentsTab(documentsTab)
    ? documentsTab
    : 'documents';

  const memberId = member?.id ?? memberIdFromRoute ?? '';
  const getDocumentsPath = useCallback(
    (tab: DocumentsTab) =>
      tab === 'documents'
        ? `${urls.app.members(memberId)}/documents`
        : `${urls.app.members(memberId)}/documents/${tab}`,
    [memberId],
  );

  React.useEffect(() => {
    if (memberId && documentsTab && !isDocumentsTab(documentsTab)) {
      history.replace(getDocumentsPath('documents'));
    }
  }, [documentsTab, getDocumentsPath, history, memberId]);

  const {
    content: fileValidationResolutionContent,
    renderRowAction: renderFileValidationRowAction,
  } = useMemberFileValidationResolution({
    member,
    memberId,
    canResolveVisibleEntryPoint: true,
    onResolutionConfirmed: () => setDocumentsListVersion(v => v + 1),
  });

  const loadDocumentExtractionTasks = useCallback(async () => {
    if (!canShowExtractionStatusColumn || !memberId || !projectId) {
      setDocumentExtractionTasks(current => (current.length ? [] : current));
      return [];
    }

    const response = await apiClient.request(
      'GET /v1/agents-v2/document-extraction-v2/tasks',
      {
        subject_id: memberId,
        project_id: projectId,
        limit: 200,
      },
    );
    setDocumentExtractionTasks(response.data.tasks);
    return response.data.tasks;
  }, [apiClient, canShowExtractionStatusColumn, memberId, projectId]);

  useEffect(() => {
    if (
      !canShowExtractionStatusColumn ||
      !accountId ||
      !memberId ||
      !projectId ||
      !serviceAuth ||
      !visibleDocumentIds.length
    ) {
      setDocumentExtractionTasks(current => (current.length ? [] : current));
      setDurableExtractedDocumentIds(current =>
        current.length ? [] : current,
      );
      setIsExtractionStatusLoading(current => (current ? false : current));
      return;
    }

    let isCancelled = false;

    const loadExtractionStatusData = async () => {
      setIsExtractionStatusLoading(true);
      try {
        await loadDocumentExtractionTasks();
        if (isCancelled) {
          return;
        }

        const extractedDocumentIds = await loadDurableExtractedDocumentIds({
          accountId,
          documentReferenceIds: visibleDocumentIds,
          memberId,
          projectId,
          serviceAuth,
        });

        if (!isCancelled) {
          setDurableExtractedDocumentIds(extractedDocumentIds);
        }
      } catch {
        if (!isCancelled) {
          setDocumentExtractionTasks([]);
          setDurableExtractedDocumentIds([]);
        }
      } finally {
        if (!isCancelled) {
          setIsExtractionStatusLoading(false);
        }
      }
    };

    loadExtractionStatusData();

    return () => {
      isCancelled = true;
    };
  }, [
    accountId,
    canShowExtractionStatusColumn,
    documentsListVersion,
    loadDocumentExtractionTasks,
    memberId,
    projectId,
    serviceAuth,
    visibleDocumentIds,
  ]);

  useEffect(() => {
    if (!documentExtractionTasks.some(isDocumentExtractionV2InFlight)) {
      return;
    }

    const interval = window.setInterval(() => {
      loadDocumentExtractionTasks().catch(() => undefined);
    }, 3000);

    return () => window.clearInterval(interval);
  }, [documentExtractionTasks, loadDocumentExtractionTasks]);

  const toggleDocumentSelection = useCallback(
    (documentReferenceId: string, isSelected: boolean) => {
      setSelectedDocumentIds(current =>
        isSelected
          ? current.includes(documentReferenceId)
            ? current
            : [...current, documentReferenceId]
          : current.filter(id => id !== documentReferenceId),
      );
    },
    [],
  );

  const handleDocumentsRowsResolved = useCallback(
    (rows: Array<{ rowMeta?: unknown }>) => {
      const documentIds = rows
        .map(row => getDocumentReferenceFromTableRow(row)?.id)
        .filter((id): id is string => !!id);

      setVisibleDocumentIds(current =>
        areStringArraysEqual(current, documentIds) ? current : documentIds,
      );
    },
    [],
  );

  const areAllVisibleDocumentsSelected =
    visibleDocumentIds.length > 0 &&
    visibleDocumentIds.every(id => selectedDocumentIds.includes(id));
  const areSomeVisibleDocumentsSelected = visibleDocumentIds.some(id =>
    selectedDocumentIds.includes(id),
  );

  const toggleVisibleDocumentSelection = useCallback(() => {
    setSelectedDocumentIds(current => {
      const visibleDocumentIdsSet = new Set(visibleDocumentIds);

      if (
        visibleDocumentIds.length > 0 &&
        visibleDocumentIds.every(id => current.includes(id))
      ) {
        return current.filter(id => !visibleDocumentIdsSet.has(id));
      }

      return Array.from(new Set([...current, ...visibleDocumentIds]));
    });
  }, [visibleDocumentIds]);

  const extractionSelectionColumns = useMemo((): TableConfiguration[] => {
    if (!canExtractDocuments) {
      return [];
    }

    const selectColumnLabel = formatMessage(
      memberDetailMessages.memberDocumentsExtractionSelectColumn,
    );

    return [
      {
        header: {
          content: () =>
            visibleDocumentIds.length ? (
              <Box className={classes.selectionCheckbox}>
                <Checkbox
                  checked={areAllVisibleDocumentsSelected}
                  indeterminate={
                    areSomeVisibleDocumentsSelected &&
                    !areAllVisibleDocumentsSelected
                  }
                  label=""
                  aria-label={formatMessage(
                    areAllVisibleDocumentsSelected
                      ? memberDetailMessages.memberDocumentsExtractionBulkClearSelection
                      : memberDetailMessages.memberDocumentsExtractionSelectAll,
                  )}
                  onChange={event => {
                    event.stopPropagation();
                    toggleVisibleDocumentSelection();
                  }}
                  onClick={event => event.stopPropagation()}
                />
              </Box>
            ) : (
              ''
            ),
          className: classes.selectionColumn,
        },
        cell: {
          className: classes.selectionColumn,
          content: (row: { rowMeta?: { resource?: fhir.Resource } }) => {
            const document = getDocumentReferenceFromTableRow(row);
            if (!document?.id) {
              return null;
            }

            return (
              <Box className={classes.selectionCheckbox}>
                <Checkbox
                  checked={selectedDocumentIds.includes(document.id)}
                  label=""
                  aria-label={selectColumnLabel}
                  onChange={event => {
                    event.stopPropagation();
                    toggleDocumentSelection(
                      document.id!,
                      event.currentTarget.checked,
                    );
                  }}
                  onClick={event => event.stopPropagation()}
                />
              </Box>
            );
          },
        },
      },
    ];
  }, [
    areAllVisibleDocumentsSelected,
    areSomeVisibleDocumentsSelected,
    canExtractDocuments,
    classes.selectionColumn,
    classes.selectionCheckbox,
    formatMessage,
    selectedDocumentIds,
    toggleDocumentSelection,
    toggleVisibleDocumentSelection,
    visibleDocumentIds.length,
  ]);

  const extractionStatusColumns = useMemo((): TableConfiguration[] => {
    if (!canShowExtractionStatusColumn) {
      return [];
    }

    const statusLabels: Record<DocumentsExtractionStatus, string> = {
      none: formatMessage(
        memberDetailMessages.memberDocumentsExtractionStatusNone,
      ),
      pending: formatMessage(
        memberDetailMessages.memberDocumentsExtractionStatusPending,
      ),
      processing: formatMessage(
        memberDetailMessages.memberDocumentsExtractionStatusProcessing,
      ),
      failed: formatMessage(
        memberDetailMessages.memberDocumentsExtractionStatusFailed,
      ),
      done: formatMessage(
        memberDetailMessages.memberDocumentsExtractionStatusDone,
      ),
    };

    return [
      {
        header: {
          label: formatMessage(
            memberDetailMessages.memberDocumentsExtractionStatusColumn,
          ),
          className: classes.tableCell,
          align: 'left',
        },
        cell: {
          align: 'left',
          className: classes.tableCell,
          content: (row: { rowMeta?: unknown }) => {
            const document = getDocumentReferenceFromTableRow(row);
            if (isExtractionStatusLoading) {
              return <DotLoader size={0} />;
            }

            const task = document?.id
              ? getDocumentExtractionTask(documentExtractionTasks, document.id)
              : null;
            const status = getDocumentExtractionStatus({
              task,
              hasDurableExtraction: document?.id
                ? durableExtractedDocumentIds.includes(document.id)
                : false,
            });

            return statusLabels[status];
          },
        },
      },
    ];
  }, [
    canShowExtractionStatusColumn,
    classes.tableCell,
    documentExtractionTasks,
    durableExtractedDocumentIds,
    formatMessage,
    isExtractionStatusLoading,
  ]);

  const handleBulkExtraction = useCallback(async () => {
    if (!selectedDocumentIds.length) {
      notify(
        formatMessage(
          memberDetailMessages.memberDocumentsExtractionBulkNoSelection,
        ),
        'warning',
      );
      return;
    }

    if (!memberId || !projectId) {
      return;
    }

    const selectionCountBucket = bucketDocumentExtractionSelectionCount(
      selectedDocumentIds.length,
    );
    setIsBulkExtracting(true);
    try {
      const tasksResponse = await apiClient.request(
        'GET /v1/agents-v2/document-extraction-v2/tasks',
        {
          subject_id: memberId,
          project_id: projectId,
          limit: 25,
        },
      );
      const result = await runBulkDocumentExtractionV2Invoke({
        apiClient,
        subjectId: memberId,
        projectId,
        documentReferenceIds: selectedDocumentIds,
        tasks: tasksResponse.data.tasks,
      });

      if (result.started > 0 && result.failed === 0) {
        notify(
          formatMessage(
            memberDetailMessages.memberDocumentsExtractionBulkSuccess,
            {
              started: result.started,
              skipped: result.skipped,
            },
          ),
          'success',
        );
      } else if (result.started > 0) {
        notify(
          formatMessage(
            memberDetailMessages.memberDocumentsExtractionBulkPartial,
            {
              started: result.started,
              skipped: result.skipped,
              failed: result.failed,
            },
          ),
          'warning',
        );
      } else {
        notify(
          formatMessage(
            memberDetailMessages.memberDocumentsExtractionBulkFailed,
            {
              skipped: result.skipped,
            },
          ),
          result.failed > 0 ? 'error' : 'warning',
        );
      }

      track('document_extraction_requested', {
        sourceType: 'document',
        mode: 'bulk',
        outcome: classifyBulkDocumentExtractionOutcome(result),
        selectionCountBucket,
      });

      if (result.started > 0) {
        setSelectedDocumentIds([]);
        if (canShowExtractionStatusColumn) {
          await loadDocumentExtractionTasks().catch(() => undefined);
        }
      }
    } catch {
      notify(
        formatMessage(
          memberDetailMessages.memberDocumentsExtractionInvokeError,
        ),
        'error',
      );
      track('document_extraction_requested', {
        sourceType: 'document',
        mode: 'bulk',
        outcome: 'error',
        selectionCountBucket,
      });
    } finally {
      setIsBulkExtracting(false);
    }
  }, [
    apiClient,
    canShowExtractionStatusColumn,
    formatMessage,
    memberId,
    notify,
    projectId,
    loadDocumentExtractionTasks,
    selectedDocumentIds,
    track,
  ]);

  const documentsHeaderActions = useMemo(() => {
    if (!canExtractDocuments || !selectedDocumentIds.length) {
      return undefined;
    }

    return (
      <Box className={classes.documentsToolbar}>
        <Button
          variant="text"
          icon={SparkleOutput}
          disabled={isBulkExtracting}
          onClick={handleBulkExtraction}
        >
          {isBulkExtracting
            ? formatMessage(
                memberDetailMessages.memberDocumentsExtractionBulkStarting,
              )
            : formatMessage(
                memberDetailMessages.memberDocumentsExtractionBulkButton,
                { count: selectedDocumentIds.length },
              )}
        </Button>
      </Box>
    );
  }, [
    canExtractDocuments,
    classes.documentsToolbar,
    formatMessage,
    handleBulkExtraction,
    isBulkExtracting,
    selectedDocumentIds.length,
  ]);

  const rowActions = useMemo(() => {
    const canDeleteClinicianProvidedMemberFiles = hasFeature(
      'fountainLifePowerUser',
    );
    return (row: { rowMeta?: { resource?: fhir.Resource } }) => {
      const resource = row.rowMeta?.resource;
      const doc =
        resource?.resourceType === 'DocumentReference'
          ? (resource as fhir.DocumentReference)
          : null;
      const canDeleteDocument = canDeleteMemberDocument(resource, {
        canDeleteClinicianProvidedMemberFiles,
      });
      const fileValidationAction = renderFileValidationRowAction(doc);
      if (
        !fileValidationAction &&
        !canDeleteDocument &&
        (!canExtractDocuments || !doc?.id)
      ) {
        return null;
      }
      return (
        <>
          {fileValidationAction}
          {canExtractDocuments && doc?.id ? (
            <Tooltip
              title={formatMessage(
                memberDetailMessages.memberDocumentsExtractionButton,
              )}
            >
              <IconButton
                icon={SparkleOutput}
                size={0}
                color="inverse"
                aria-label={formatMessage(
                  memberDetailMessages.memberDocumentsExtractionButton,
                )}
                onClick={event => {
                  event.stopPropagation();
                  event.currentTarget.blur();
                  setExtractionTarget(doc);
                }}
              />
            </Tooltip>
          ) : null}
          {canDeleteDocument && doc ? (
            <Tooltip
              title={formatMessage(
                memberDetailMessages.memberDocumentsDeleteButton,
              )}
            >
              <IconButton
                icon={Trash}
                size={0}
                color="inverse"
                aria-label={formatMessage(
                  memberDetailMessages.memberDocumentsDeleteButton,
                )}
                onClick={event => {
                  event.stopPropagation();
                  event.currentTarget.blur();
                  setDeleteTarget(doc);
                }}
              />
            </Tooltip>
          ) : null}
        </>
      );
    };
  }, [
    canExtractDocuments,
    formatMessage,
    hasFeature,
    renderFileValidationRowAction,
  ]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget?.id || !accountId || !serviceAuth?.authToken) {
      return;
    }
    try {
      await deleteDocument(accountId, deleteTarget.id);
      setDeleteTarget(null);
      resetDelete();
      setDocumentsListVersion(v => v + 1);
      notify(
        formatMessage(memberDetailMessages.memberDocumentsDeleteSuccess),
        'success',
      );
    } catch {
      // Error surfaced via deleteState below
    }
  }, [
    accountId,
    deleteDocument,
    deleteTarget,
    formatMessage,
    notify,
    resetDelete,
    serviceAuth,
  ]);

  if (!member) {
    return <>{fallback}</>;
  }

  const patient = { ...member, id: member.id ?? '' } as fhir.Patient & {
    id: string;
  };

  return (
    <div className={classes.root}>
      <div className={classes.tabsContainer}>
        <Tabs selectedTabId={selectedTab} variant="pill">
          <div className={classes.tabsRoot}>
            <div className={classes.tabListWrapper}>
              <TabList
                aria-label="Documents, notes, medications, procedures, conditions, allergies, and immunizations sections"
                className={classes.tabList}
              >
                <Tab
                  stopId="documents"
                  onClick={() => history.push(getDocumentsPath('documents'))}
                >
                  {formatMessage(memberDetailMessages.documents)}
                </Tab>
                <Tab
                  stopId="notes"
                  onClick={() => history.push(getDocumentsPath('notes'))}
                >
                  {formatMessage(memberDetailMessages.notes)}
                </Tab>
                <Tab
                  stopId="medications"
                  onClick={() => history.push(getDocumentsPath('medications'))}
                >
                  {formatMessage(memberDetailMessages.medications)}
                </Tab>
                <Tab
                  stopId="procedures"
                  onClick={() => history.push(getDocumentsPath('procedures'))}
                >
                  {formatMessage(memberDetailMessages.procedures)}
                </Tab>
                <Tab
                  stopId="conditions"
                  onClick={() => history.push(getDocumentsPath('conditions'))}
                >
                  {formatMessage(memberDetailMessages.conditions)}
                </Tab>
                <Tab
                  stopId="allergies"
                  onClick={() => history.push(getDocumentsPath('allergies'))}
                >
                  {formatMessage(memberDetailMessages.allergies)}
                </Tab>
                <Tab
                  stopId="immunizations"
                  onClick={() =>
                    history.push(getDocumentsPath('immunizations'))
                  }
                >
                  {formatMessage(memberDetailMessages.immunizations)}
                </Tab>
              </TabList>
            </div>
            <TabPanel stopId="documents" className={classes.tabPanel}>
              <DocumentExtractionV2Modal
                isOpen={!!extractionTarget}
                document={extractionTarget}
                subjectId={memberId}
                onDismiss={() => setExtractionTarget(null)}
              />
              {fileValidationResolutionContent}
              <Modal
                isOpen={!!deleteTarget}
                onDismiss={() => {
                  setDeleteTarget(null);
                  resetDelete();
                }}
                title={formatMessage(
                  memberDetailMessages.memberDocumentsDeleteConfirmTitle,
                )}
                actions={
                  <>
                    <Button
                      variant="text"
                      onClick={() => {
                        setDeleteTarget(null);
                        resetDelete();
                      }}
                    >
                      {formatMessage(
                        memberDetailMessages.memberDocumentsDeleteCancel,
                      )}
                    </Button>
                    <Button
                      variant="contained"
                      disabled={deleteState.isFetching}
                      onClick={confirmDelete}
                    >
                      {formatMessage(
                        memberDetailMessages.memberDocumentsDeleteConfirm,
                      )}
                    </Button>
                  </>
                }
              >
                <Text>
                  {formatMessage(
                    memberDetailMessages.memberDocumentsDeleteConfirmBody,
                  )}
                </Text>
                {deleteState.error ? (
                  <Text>{deleteState.error.message}</Text>
                ) : null}
              </Modal>
              <SubjectViewerTableModule
                key={documentsListVersion}
                isModuleVisible={true}
                dateTickFilter={{ min: 0, max: 0 }}
                dateTickBounds={{ min: 0, max: 0 }}
                dateFilter={{ min: new Date(0), max: new Date() }}
                isInEditMode={false}
                deviceId="member-detail"
                onModuleConfigChange={() => undefined}
                onDynamicDataResolved={() => ({})}
                selectedDashboardConfig={documentsDashboardConfig}
                moduleKey={documentsModuleConfig.module.key}
                moduleConfig={documentsModuleConfig}
                patient={patient}
                dataSource={documentsModuleConfig.component.dataSource}
                columns={documentsModuleConfig.component.columns}
                momentFormat={documentsModuleConfig.component.momentFormat}
                pageSize={documentsModuleConfig.component.pageSize}
                useTagFilter={documentsModuleConfig.component.useTagFilter}
                useNotTagFilter={
                  documentsModuleConfig.component.useNotTagFilter
                }
                notTagFilters={documentsModuleConfig.component.notTagFilters}
                categories={documentsModuleConfig.component.categories}
                useCategoryFilter={
                  documentsModuleConfig.component.useCategoryFilter
                }
                timeFilterProps={
                  documentsModuleConfig.component.timeFilterProps
                }
                applyDateFilter={
                  documentsModuleConfig.component.applyDateFilter
                }
                filters={documentsModuleConfig.component.filters}
                filterResources={shouldRenderInMemberDocumentsTable}
                prependColumns={extractionSelectionColumns}
                appendColumns={extractionStatusColumns}
                headerActions={documentsHeaderActions}
                tableClassName={classes.documentsTable}
                onRowsResolved={handleDocumentsRowsResolved}
                rowActions={rowActions}
                refetchDependencies={[documentsListVersion]}
              />
            </TabPanel>
            <TabPanel stopId="notes" className={classes.tabPanel}>
              <NotesContent memberId={memberId} fallback={fallback} />
            </TabPanel>
            <TabPanel stopId="medications" className={classes.tabPanel}>
              <MedicationsContent memberId={memberId} fallback={fallback} />
            </TabPanel>
            <TabPanel stopId="procedures" className={classes.tabPanel}>
              <ProceduresContent memberId={memberId} fallback={fallback} />
            </TabPanel>
            <TabPanel stopId="conditions" className={classes.tabPanel}>
              <ConditionsContent memberId={memberId} fallback={fallback} />
            </TabPanel>
            <TabPanel stopId="allergies" className={classes.tabPanel}>
              <AllergiesContent memberId={memberId} fallback={fallback} />
            </TabPanel>
            <TabPanel stopId="immunizations" className={classes.tabPanel}>
              <ImmunizationsContent memberId={memberId} fallback={fallback} />
            </TabPanel>
          </div>
        </Tabs>
      </div>
    </div>
  );
};

const MemberDocuments: React.FC = () => {
  return <MemberDetail section="documents" />;
};

MemberDocuments.displayName = 'MemberDocuments';
export default MemberDocuments;
